import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  Sparkles,
  Building2,
  ArrowRight,
  Clock,
  AlertTriangle,
  Shield,
  XCircle,
  RotateCcw,
} from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, dateTR, numberTR } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../lib/auth";

function StatusBanner({ sub }) {
  if (!sub) return null;
  const canceled = !!sub.canceled_at;
  const conf = {
    active: canceled
      ? {
          tone: "bg-amber-50 border-amber-200 text-amber-900",
          icon: <AlertTriangle className="w-5 h-5" />,
          title: `Abonelik iptal edildi — ${dateTR(sub.subscription_valid_until)} tarihine kadar aktif`,
          msg: `Bu tarihten sonra otomatik yenileme yapılmayacak (${sub.days_left} gün kaldı). Devam etmek istersen aşağıdan aboneliği yeniden aktifleştirebilirsin.`,
        }
      : {
          tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
          icon: <Shield className="w-5 h-5" />,
          title: `Aktif abonelik: ${sub.plan ? sub.plan[0].toUpperCase() + sub.plan.slice(1) : ""}`,
          msg: sub.subscription_valid_until
            ? `Bir sonraki yenileme tarihi: ${dateTR(sub.subscription_valid_until)} (${sub.days_left} gün kaldı)`
            : "",
        },
    trial: {
      tone: "bg-amber-50 border-amber-200 text-amber-900",
      icon: <Sparkles className="w-5 h-5" />,
      title: `Ücretsiz deneme sürüyor — ${sub.trial_days_left} gün kaldı`,
      msg: `Deneme ${dateTR(sub.trial_end)} tarihinde sona erecek. Ödemesiz kullanmaya devam edin.`,
    },
    expired: {
      tone: "bg-rose-50 border-rose-200 text-rose-900",
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "Aboneliğiniz sona erdi",
      msg: "Yeni araç eklemeye devam etmek için lütfen bir plan seçin.",
    },
    trial_expired: {
      tone: "bg-rose-50 border-rose-200 text-rose-900",
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "Deneme süresi doldu",
      msg: "Panele erişmeye devam etmek için bir plan seçin.",
    },
    none: {
      tone: "bg-stone-50 border-stone-200 text-stone-800",
      icon: <Clock className="w-5 h-5" />,
      title: "Abonelik bilgisi yok",
      msg: "Bir plan seçerek başlayın.",
    },
  }[sub.status];
  if (!conf) return null;
  return (
    <div
      data-testid="subscription-status-banner"
      className={`rounded-2xl border p-4 flex items-start gap-3 ${conf.tone}`}
    >
      <div className="mt-0.5">{conf.icon}</div>
      <div>
        <div className="font-display font-black text-lg">{conf.title}</div>
        <div className="text-sm mt-1 opacity-90">{conf.msg}</div>
      </div>
    </div>
  );
}

function PlanCard({ plan, current, sub, onSubscribe, busy, consent }) {
  const isCurrent = current === plan.key && sub?.status === "active";
  const overLimit =
    plan.limit !== null && sub?.vehicle_count > plan.limit;
  const label = plan.limit === null ? "sınırsız araç" : `${plan.limit} araç`;
  return (
    <div
      data-testid={`plan-${plan.key}`}
      className={`relative rounded-2xl border p-6 transition-all flex flex-col ${
        plan.key === "pro"
          ? "border-orange-600 shadow-lg shadow-orange-100/60 bg-white"
          : "border-stone-200 bg-white"
      }`}
    >
      {plan.key === "pro" && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-700 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
          En popüler
        </div>
      )}
      <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
        {plan.name}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <div className="font-display font-black text-4xl text-slate-900 num-tabular">
          {currencyTRY(plan.price)}
        </div>
        <span className="text-sm text-stone-500">/ ay</span>
      </div>
      <div className="mt-1 text-xs text-stone-500">KDV dahil · Aylık ödeme</div>
      <div className="mt-4 text-sm text-stone-700 font-medium">
        Aylık Galeri Yönetim Paketi — {label}
      </div>
      <ul className="mt-4 space-y-2 text-sm text-stone-700 flex-1">
        {[
          plan.limit === null ? "Sınırsız araç envanteri" : `${plan.limit} araç envanter kapasitesi`,
          "Sınırsız kiralama & iade",
          "Aylık kâr/zarar analitiği",
          "Araç fotoğraf yükleme",
          "WhatsApp iade hatırlatmaları",
          "Sınırsız üye & yönetici",
        ].map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
            {f}
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <div className="mt-6 h-11 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-semibold text-sm">
          Şu anki planınız
        </div>
      ) : (
        <button
          data-testid={`subscribe-${plan.key}`}
          disabled={busy || overLimit || !consent}
          onClick={() => onSubscribe(plan.key)}
          title={!consent ? "Önce Mesafeli Satış Sözleşmesi kutucuğunu işaretleyin" : ""}
          className={`mt-6 h-11 rounded-full font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
            plan.key === "pro"
              ? "bg-orange-700 hover:bg-orange-800 text-white"
              : "border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"
          }`}
        >
          {busy
            ? "Yönlendiriliyor…"
            : overLimit
              ? "Araç sayınız bu plana sığmıyor"
              : `Satın Al — ${currencyTRY(plan.price)}/ay`}
          {!busy && !overLimit && <ArrowRight className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
}

export default function Subscription() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/subscription");
      setSub(res.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const subscribe = async (plan) => {
    if (!consent) {
      toast.error("Devam etmek için Mesafeli Satış Sözleşmesi kutucuğunu işaretleyin");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/subscription/iyzico/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const res = await api.post("/subscription/cancel");
      setSub(res.data);
      setCancelOpen(false);
      toast.success(
        res.data.subscription_valid_until
          ? `Abonelik iptal edildi. ${dateTR(res.data.subscription_valid_until)} tarihine kadar kullanabilirsin.`
          : "Abonelik iptal edildi."
      );
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const res = await api.post("/subscription/reactivate");
      setSub(res.data);
      toast.success("Otomatik yenileme yeniden aktifleştirildi.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setReactivating(false);
    }
  };

  if (loading) return <Skeleton className="h-96 rounded-2xl" />;
  if (!sub) return null;

  const usagePct =
    sub.vehicle_limit && sub.vehicle_limit > 0
      ? Math.min(100, (sub.vehicle_count / sub.vehicle_limit) * 100)
      : sub.vehicle_limit === null
        ? 0
        : 100;

  return (
    <div data-testid="subscription-page">
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
          Abonelik
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
          Galeriniz için doğru plan
        </h1>
        <p className="text-stone-500 text-sm mt-2 max-w-2xl">
          14 gün ücretsiz deneme dahil. Araç sayınıza göre bir plan seçin;
          istediğiniz zaman değiştirebilirsiniz.
        </p>
      </div>

      <StatusBanner sub={sub} />

      {/* Usage */}
      <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            Araç Kullanımı
          </div>
          <div className="text-sm text-stone-700 num-tabular">
            <b className="text-slate-900 font-display">
              {numberTR(sub.vehicle_count)}
            </b>{" "}
            /{" "}
            {sub.vehicle_limit === null ? (
              <span className="text-emerald-700">Sınırsız</span>
            ) : (
              numberTR(sub.vehicle_limit)
            )}
          </div>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${
              usagePct >= 90 ? "bg-rose-600" : usagePct >= 60 ? "bg-amber-500" : "bg-emerald-600"
            }`}
            style={{ width: `${usagePct}%` }}
          />
        </div>
      </div>

      {/* Manage subscription (admin only, when there's something to manage) */}
      {user?.role === "admin" && (sub.status === "active" || sub.status === "trial") && (
        <div
          data-testid="manage-subscription-card"
          className="mt-6 rounded-2xl border border-stone-200 bg-white p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div className="flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
              Abonelik Yönetimi
            </div>
            <div className="mt-1 font-display font-black text-lg text-slate-900">
              {sub.canceled_at
                ? "İptal edildi — yenileme yapılmayacak"
                : sub.status === "trial"
                  ? "Ücretsiz deneme sürüyor"
                  : "Aylık otomatik yenileme aktif"}
            </div>
            <div className="text-sm text-stone-600 mt-1">
              {sub.canceled_at
                ? `Erişim ${dateTR(sub.subscription_valid_until)} tarihine kadar açık kalacak.`
                : sub.status === "trial"
                  ? `Denemen ${dateTR(sub.trial_end)} tarihinde biter. İstediğin an iptal edebilirsin.`
                  : `Bir sonraki yenileme ${dateTR(sub.subscription_valid_until)}. Dilediğin an iptal edebilirsin — kalan sürede aktif kalırsın.`}
            </div>
          </div>
          {sub.canceled_at && sub.status === "active" ? (
            <button
              data-testid="reactivate-subscription-btn"
              onClick={handleReactivate}
              disabled={reactivating}
              className="h-11 px-5 rounded-full font-semibold text-sm bg-emerald-700 hover:bg-emerald-800 text-white transition-colors disabled:opacity-60 flex items-center gap-2 whitespace-nowrap"
            >
              <RotateCcw className="w-4 h-4" />
              {reactivating ? "İşleniyor…" : "Aboneliği Yeniden Aktifleştir"}
            </button>
          ) : !sub.canceled_at ? (
            <button
              data-testid="cancel-subscription-btn"
              onClick={() => setCancelOpen(true)}
              className="h-11 px-5 rounded-full font-semibold text-sm border border-rose-300 text-rose-700 hover:bg-rose-50 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <XCircle className="w-4 h-4" />
              Aboneliği İptal Et
            </button>
          ) : null}
        </div>
      )}

      {/* Plans */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        {sub.plans.map((p) => (
          <PlanCard
            key={p.key}
            plan={p}
            current={sub.plan}
            sub={sub}
            onSubscribe={subscribe}
            busy={busy}
            consent={consent}
          />
        ))}
      </div>

      {/* Consent checkbox */}
      <div
        className={`mt-8 rounded-2xl border p-4 flex items-start gap-3 ${
          consent
            ? "border-emerald-200 bg-emerald-50/40"
            : "border-stone-200 bg-white"
        }`}
        data-testid="consent-box"
      >
        <input
          id="consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="consent-checkbox"
          className="mt-0.5 w-5 h-5 accent-orange-700 shrink-0"
        />
        <label htmlFor="consent" className="text-sm text-stone-700 cursor-pointer flex-1">
          <b>Mesafeli Satış Sözleşmesi</b>'ni okudum ve onaylıyorum. Ayrıca{" "}
          <Link to="/gizlilik" target="_blank" className="underline text-orange-700">
            Gizlilik ve Güvenlik Politikası
          </Link>
          {" ve "}
          <Link to="/iptal-iade" target="_blank" className="underline text-orange-700">
            İptal ve İade Koşulları
          </Link>
          'nı kabul ediyorum.{" "}
          <Link to="/mesafeli-satis-sozlesmesi" target="_blank" className="underline text-orange-700">
            Sözleşmeyi oku →
          </Link>
        </label>
      </div>

      <p className="mt-6 text-xs text-stone-500 text-center max-w-2xl mx-auto">
        Ödemeler Iyzico güvencesindedir. Aylık planınız satın alma tarihinden
        itibaren 30 gün geçerlidir; süre bitiminde panelden manuel olarak
        yenileyebilirsiniz. Kart bilgileri Iyzico üzerinde saklanır,
        panelimizde tutulmaz.
      </p>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Aboneliği iptal etmek istediğine emin misin?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-stone-600">
                <p>
                  {sub.status === "trial" ? (
                    <>
                      Ücretsiz denemen <b>hemen sona erecek</b> ve panele erişimin
                      kapanacak. Devam etmek istersen daha sonra bir plan seçerek
                      yeniden başlayabilirsin.
                    </>
                  ) : (
                    <>
                      Aboneliğin <b>{dateTR(sub.subscription_valid_until)}</b>{" "}
                      tarihine kadar aktif kalmaya devam edecek ({sub.days_left} gün).
                      Bu tarihten sonra otomatik yenileme <b>yapılmayacak</b> ve
                      araç ekleme durdurulacak.
                    </>
                  )}
                </p>
                <p>Fikrin değişirse aynı yerden yeniden aktifleştirebilirsin.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-back-btn" disabled={canceling}>
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cancel-dialog-confirm-btn"
              disabled={canceling}
              onClick={handleCancel}
              className="bg-rose-700 hover:bg-rose-800"
            >
              {canceling ? "İptal ediliyor…" : "Evet, iptal et"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
